import Vue from 'vue'
import {Component, Prop} from 'vue-property-decorator';
import {namespace} from 'vuex-class';
import {ActivityStatus} from '../../../../common/enums';
import {USER_MODULE_NAMESPACE} from '../../../../store/user/user-types.store';
import {IUserDetails} from '../../../../models/user';
import {
  BM_DELETE_MARKER, BM_EDIT_MARKER_BY_FIELD,
  BM_GET_MARKERS,
  BM_MODULE_NAMESPACE, BM_POST_MARKER
} from "../../../../store/building-monitor/bm-types.store";
import {StringIdGenerator} from '../../../../utils/StringIdGenerator'
import {IBMMeetingDetailViewModel} from "../../../../models/bm/bm-meeting-detail.viewmodel";
import {IBMMarkerEditedViewModel} from "../../../../models/bm/bm-marker-edited.viewmodel";

const UserStore = namespace(USER_MODULE_NAMESPACE);
const BMStore = namespace(BM_MODULE_NAMESPACE)

@Component({
  components: {}
})
class BMPlanMarkerModalVertical extends Vue {

  private _clearInterval: any;

  dtRules: {
    participantRules?: any
  }
  dtSelectedPlan?: any
  canvas: any
  ctx: any
  mapSprite: any
  markers: any[]
  baseMarker: any
  plan: {
    width: number
    height: number
  }
  screen: {
    width: number
    height: number
  }
  markerSize: number
  scale: number
  scaleMultiplier: number
  minScale: number
  maxScale: number
  displaySlider: boolean
  idsGenerator: StringIdGenerator
  markerLoading: boolean

  constructor() {
    super();
    this.dtRules = {};
    this.dtSelectedPlan = null
    this.canvas = null
    this.ctx = null
    this.mapSprite = null
    this.markers = []
    this.baseMarker = {}
    this.plan = {
      width: 0,
      height: 0
    }
    this.screen = {
      width: 0,
      height: 0
    }
    this.markerSize = 10
    this.scale = 1
    this.scaleMultiplier = 0.01
    this.minScale = 0.01
    this.maxScale = 3.0
    this.displaySlider = false
    this.idsGenerator = null
    this.markerLoading = false
  }

  @UserStore.Getter userInfo: IUserDetails;
  @BMStore.Getter bmMeetingPlan: any[]
  @BMStore.Getter bmMeeting: { activityStatus: ActivityStatus, data: IBMMeetingDetailViewModel };
  @BMStore.Getter bmMarkerEdited: IBMMarkerEditedViewModel

  @BMStore.Action(BM_GET_MARKERS)
  storeGetMarker: (payload: { bmid: string, idm: string, idp: string }) => Promise<any>;
  @BMStore.Action(BM_POST_MARKER)
  storePostMarker: (payload: { bmid: string, idm: string, idp: string, params: object }) => Promise<any>;
  @BMStore.Action(BM_DELETE_MARKER)
  storeDeleteMarker: (payload: { bmid: string, idm: string, idp: string, idmk: string }) => Promise<any>;

  @BMStore.Mutation(BM_EDIT_MARKER_BY_FIELD)
  storeSetBMMarkerEdited: (payload: { [key: string]: any }) => void

  @Prop({default: false, type: [Boolean]})
  disabled: boolean

  @Prop({default: null, type: [Object]})
  params: any

  get lblSetting() {
    return {
      ...require('@/locales/ticket').default,
      ...require('@/locales/building-monitor').default,
      ...require('@/locales/validation-messages').default,
      ...require('@/locales/generic-labels').default,
    }
  }

  get calcWidth() {
    return this.plan.width * this.scale
  }

  get calcHeight() {
    return this.plan.height * this.scale
  }

  get meeting() {
    return this.bmMeeting.data
  }

  get inspection() {
    return this.params.inspection
  }

  created() {
    if (this.bmMeetingPlan.length) {
      this.dtSelectedPlan = this.bmMeetingPlan[0]
    }
  }

  mounted() {
    this.$nextTick(() => {
      this.idsGenerator = new StringIdGenerator()
      this.getAPIMarker()
    })
  }

  updatePlanSelected(val) {
    this.dtSelectedPlan = val
    this.idsGenerator = new StringIdGenerator()
    this.getAPIMarker()
  }

  deleteMarker() {
    if (this.disabled) return
    let cmtMarkers = this.markers.filter(m => {
      return parseInt(m.hint_marker.replace(/[^0-9]/g,''), 10) === this.params.cmt.hint_comment
    })
    if (cmtMarkers.length) {
      this.markerLoading = true
      this.deleteAPIMarker(cmtMarkers[0])
      let indexM = this.markers.findIndex(m => m.id === cmtMarkers[0].id)
      this.markers.splice(indexM, 1)
      this.idsGenerator.backIndex()
    }
  }

  allowDraw() {
    if (this.disabled) return
    this.canvas.addEventListener("mousedown", this.addMarkerClick, false)
  }

  roundMarker(x, y, hint = null) {
    return {
      position_x: x,
      position_y: y,
      hint_marker: hint ? hint : (this.params.cmt.hint_comment + this.idsGenerator.next()),
      fk_bm_ib_comment: this.params.cmt.id,
      color: this.inspection.color
    }
  }

  addMarkerClick(mouse) {
    if (this.disabled) return
    let rect = this.canvas.getBoundingClientRect()
    let mouseXPos = (mouse.x - rect.left) / this.scale
    let mouseYPos = (mouse.y - rect.top) / this.scale
    let marker = this.roundMarker(mouseXPos, mouseYPos)
    this.markers.unshift(marker)
    this.addAPIMarker(marker)
  }

  firstLoad() {
    this.canvas = document.getElementById('Canvas')
    this.ctx = this.canvas.getContext("2d")
    this.mapSprite = new Image()
    this.mapSprite.src = this.dtSelectedPlan.media.link
    this.plan.width = this.dtSelectedPlan.media.width || this.mapSprite.width
    this.plan.height = this.dtSelectedPlan.media.height || this.mapSprite.height
    this.screen.width = window.screen.width //710
    this.screen.height = window.screen.height
    this.ctx.textAlign = "center"
    this.minScale = (window.screen.width * 0.7) / this.plan.width
    this.scale = this.minScale
    this.displaySlider = true
    this.markerSize = (this.plan.width * 2) / 100
    clearInterval(this._clearInterval)
    this._clearInterval = setInterval(this.draw, (1000 / 60)) // 60 FPS
    this.allowDraw()
  }

  draw() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.ctx.drawImage(this.mapSprite, 0, 0, this.plan.width, this.plan.height, 0, 0, this.canvas.width, this.canvas.height)
    for (let i = 0; i < this.markers.length; i++) {
      let tempMarker = this.markers[i]
      let markerText = tempMarker.hint_marker
      this.ctx.fillStyle = '#' + tempMarker.color//"#2E038C"
      this.ctx.beginPath()
      this.ctx.arc(tempMarker.position_x * this.scale, tempMarker.position_y * this.scale, this.markerSize * this.scale, 0, 2 * Math.PI)
      this.ctx.closePath()
      this.ctx.fill()
      this.ctx.fillStyle = "#FFFFFF"
      this.ctx.font = this.ctx.font.replace(/\d+px/, (Math.floor(this.markerSize * this.scale)) + 'px');
      let textMeasurements = this.ctx.measureText(markerText)
      this.ctx.fillText(markerText, (tempMarker.position_x - ((textMeasurements.width / this.scale) / 2)) * this.scale, (tempMarker.position_y + (this.markerSize / 3)) * this.scale)
    }
  }

  async getAPIMarker() {
    this.storeGetMarker({
      bmid: this.$route.params.id,
      idm: this.$route.params.idm,
      idp: this.dtSelectedPlan.id
    })
      .then(response => {
        this.markers = response.markers
        let cmtMarkers = this.markers.filter(m => parseInt(m.hint_marker.replace(/[^0-9]/g,''), 10) === this.params.cmt.hint_comment)
        if (cmtMarkers.length) {
          let charNew = cmtMarkers[0].hint_marker.replace(this.params.cmt.hint_comment, '')
          this.idsGenerator.changeNextId(charNew)
        }
        this.firstLoad()
      })
  }

  async addAPIMarker(marker) {
    this.markerLoading = true
    this.storePostMarker({
      bmid: this.$route.params.id,
      idm: this.$route.params.idm,
      idp: this.dtSelectedPlan.id,
      params: marker
    })
      .then(newMarker => {
        this.markers = this.markers.map(x => {
          if (x.hint_marker === newMarker.hint_marker) x = newMarker
          return x
        })
      })
      .finally(() => {
        this.markerLoading = false
      })
  }

  async deleteAPIMarker(marker) {
    this.markerLoading = true
    this.storeDeleteMarker({
      bmid: this.$route.params.id,
      idm: this.$route.params.idm,
      idp: this.dtSelectedPlan.id,
      idmk: marker.id
    }).finally(() => {
      this.markerLoading = false
    })
  }
}

export default BMPlanMarkerModalVertical;



// WEBPACK FOOTER //
// ./src/containers/building-monitor/components/bm-plan-marker-modal-vertical/index.ts